import React, { useState } from 'react'
import { Platform } from 'react-native'

import { useQueryClient } from '@tanstack/react-query'
import { MY_TEAMS_ADVISOR } from '@zet/constants'
import {
  B2,
  B4,
  B5,
  BTN_SM,
  Icon,
  SH3,
  SH4,
  Stack,
  XStack,
  YStack,
  colors,
  colors as zetColors,
} from '@zet/design-system'
import Pressable from '@zet/design-system/pressable'
import { APP_NAME } from '@zet/end-points'
import { i18n } from '@zet/localization'
import DropdownFilled from '@zet/svg/dropdownFilled.svg'
import InterestRateIcon from '@zet/svg/loanBrandCard/interestRate.svg'
import MoneyBag from '@zet/svg/loanBrandCard/moneyBag.svg'
import OfferTag from '@zet/svg/offerTag.svg'
import { DYNAMIC_REJECTION_PAYOUT } from '@zet/utils/Constants'
import { isCustomerFlow } from '@zet/utils/isCustomerFlow'
import { navigationRef } from '@zet/utils/navigation'
import { titleCase } from '@zet/utils/stringConversion'
import { isEmpty } from 'lodash'

import {
  useBrandDetailsById,
  useGetLoyaltyTierDetails,
} from '../../api/brands/hooks'
import { getApprovalChanceColor, getRecommendationReason } from '../../utils'
import {
  DEMO_BRAND_PARTNER_ID,
  REASON_LIST,
  REJECTION_PAYOUT,
  rejectionPayoutCategoryList,
} from '../../utils/constants'
import { IBrandListRoute, IProductCard } from '../../utils/interface'
import BrandDetailsBottomSheet from '../brandDetailsBottomSheet'
import { BrandTitle, GradientView } from './productCardHelperComponent'
import WebApplyButton from './webApplyButton'

const CardOnCardText = ({ isInEligible }: { isInEligible?: boolean }) => (
  <B5
    style={{
      color: isInEligible ? zetColors.grays[60] : zetColors.decorative.bee[150],
    }}
  >
    {i18n.t('recommendationCard.cardOnCard')}
  </B5>
)

const ProductCard = ({
  isCardOnCard,
  partnerId,
  customerId,
  isRecommendationFlow,
  isInEligible,
  criteria,
  reason,
  isMoreCard,
  isRenderTag,
  inEligiblePartners,
  alreadyApplied,
  isSourceWeb,
  dynamicRejectionPayout,
  approvalRate,
  approvalRateTextColor,
  onHold,
  index,
  leadSource,
  isLeadPageCrossSelling,
  isHighApproval,
  tier,
  customerPhoneNumber,
  leadAdded,
  customerName,
  route,
  isTryDemoSellFlow,
  showViewBenefits = false,
  loyaltyPayout,
}: IProductCard) => {
  const params: IBrandListRoute = route.params as IBrandListRoute
  const { data: content } = useBrandDetailsById(partnerId)
  const { data: tierData } = useGetLoyaltyTierDetails()
  const queryClient = useQueryClient()
  const [isBrandDetailBSOpen, setIsBrandDetailBSOpen] = useState(false)

  const handleViewAllPress = () => {
    queryClient.setQueryData(
      [
        'recommendationIneligible',
        content?.categoryId,
        params.customerId || customerId,
      ],
      inEligiblePartners
    )
    navigationRef.navigate('InEligibleBrandsList', {
      customerName: params?.customerName || customerName,
      customerPhoneNumber: params?.customerPhoneNumber || customerPhoneNumber,
      customerId: params?.customerId || customerId,
      categoryId: content?.categoryId,
      listData: inEligiblePartners,
    })
  }
  const handleProductCardPress = () => {
    if (isCustomerFlow()) {
      return false
    }
    const routeObject = {
      brandId: partnerId,
      customerName: params?.customerName || customerName,
      customerPhoneNumber: params?.customerPhoneNumber || customerPhoneNumber,
      customerId: params?.customerId || customerId,
      isEligible: !isInEligible,
      brandDetails: true,
      routeName: isRecommendationFlow ? 'forCustomerFlow' : 'category',
      index: index + 1,
      leadSource,
      tierData,
      ...(isInEligible
        ? {
            reason: criteria
              ? getRecommendationReason(
                  criteria,
                  reason,
                  titleCase((params?.customerName as string) || customerName),
                  content?.name
                )
              : reason,
            criteria,
          }
        : { dynamicRejectionPayout, approvalRate, approvalRateTextColor }),
    }
    if (isInEligible) {
      navigationRef.navigate('BrandDetails', routeObject)
    } else {
      navigationRef.navigate('BrandDetails', routeObject)
    }
  }

  const getIcon = (type: string) => {
    switch (type) {
      case 'bag':
        return <MoneyBag />
      case 'interestRate':
        return <InterestRateIcon />
      default:
        return <OfferTag />
    }
  }

  if (isEmpty(content)) {
    return <></>
  }

  return (
    <Stack className="mb-3">
      <Pressable
        className="border-grays-40 mx-3 rounded-lg border bg-white pt-0"
        onPress={handleProductCardPress}
        eventPayload={{
          item_name: isRecommendationFlow
            ? 'brands_list_recommendation_brand_card'
            : 'brands_list_brand_card',
          brand_name: content?.name,
          brand_category: content?.subCategory,
          isLeadPageCrossSelling,
        }}
        eventName="click_action"
      >
        <GradientView
          partnerId={partnerId}
          isRenderTag={isRenderTag}
          onHold={onHold}
          isHighApproval={isRecommendationFlow ? isHighApproval : false}
          content={content}
          tierData={tierData}
          tier={tier}
          isRecommendationFlow={isRecommendationFlow}
          loyaltyPayout={loyaltyPayout}
        />
        <YStack className="px-3 pb-3">
          <BrandTitle name={content?.name} />
          {Boolean(isRecommendationFlow && approvalRate) && (
            <SH3
              className={getApprovalChanceColor(
                approvalRateTextColor as string
              )}
            >
              {i18n.t('recommendationCard.approvalChance', {
                initialText: approvalRate,
              })}
            </SH3>
          )}

          {Boolean(isRecommendationFlow && isInEligible) && (
            <SH4 className="text-error-100">
              {i18n.t('recommendationCard.approvalChance', {
                initialText: 'Less than 1%',
              })}
            </SH4>
          )}

          {isInEligible ? (
            <Stack>
              {Boolean(isCardOnCard) && <CardOnCardText isInEligible />}
              <XStack className="bg-error-10 mt-4/2 items-center space-x-2 rounded-md p-2">
                <Icon
                  type="DocumentInvalidOutlined"
                  size={24}
                  color={zetColors.error[200]}
                />
                <B5 className="text-error-200 flex-1">
                  {criteria
                    ? getRecommendationReason(
                        criteria,
                        reason,
                        titleCase(
                          (params?.customerName as string) || customerName
                        ),
                        content?.name
                      )
                    : reason}
                </B5>
              </XStack>
            </Stack>
          ) : (
            <>
              <Stack className="mt-4/2 mb-1">
                {content?.brandSpecification ? (
                  <>
                    {content?.brandSpecification?.map(
                      (item, index) =>
                        index < 2 && (
                          <XStack key={index} className="space-x-1">
                            <Stack>{getIcon(item?.iconType)}</Stack>
                            <B2 className="text-grays-60 mb-2 flex-1">
                              {item?.description}
                            </B2>
                          </XStack>
                        )
                    )}
                  </>
                ) : (
                  <>
                    {content?.topFeatures?.map(
                      (value, index) =>
                        index < 2 && (
                          <XStack key={index} className="space-x-1">
                            <Stack>
                              <OfferTag />
                            </Stack>
                            <B2 className="text-grays-60 mb-2 flex-1">
                              {value}
                            </B2>
                          </XStack>
                        )
                    )}
                  </>
                )}
                {/* {content?.topFeatures?.map(
                  (value, index) =>
                    index < 2 && (
                      <XStack key={index} className="space-x-1">
                        <Stack>
                          <OfferTag />
                        </Stack>
                        <B2 className="text-grays-60 mb-2 flex-1">{value}</B2>
                      </XStack>
                    )
                )} */}
              </Stack>
              {!isCustomerFlow() && (
                <Pressable
                  className="mb-2 flex-row items-center space-x-2"
                  onPress={handleProductCardPress}
                >
                  <Stack>
                    <DropdownFilled color={colors.primary[100]} />
                  </Stack>
                  <BTN_SM className="text-primary-100">
                    {i18n.t('recommendationCard.benefitsAndDetails')}
                  </BTN_SM>
                </Pressable>
              )}
              {false &&
                !isCustomerFlow() &&
                !isTryDemoSellFlow &&
                APP_NAME !== MY_TEAMS_ADVISOR &&
                dynamicRejectionPayout !== 0 &&
                rejectionPayoutCategoryList.indexOf(
                  content?.categoryId?.toString() as string
                ) > -1 && (
                  <XStack className="border-grays-40 pt-4/2 mt-2 items-center space-x-2 border-t border-dashed">
                    <Icon
                      type="RupeeCircleOutlined"
                      size={20}
                      color={zetColors.error[150]}
                    />
                    <B4 className="text-error-200">
                      {i18n.t('recommendationCard.get')}
                      {isRecommendationFlow
                        ? dynamicRejectionPayout
                          ? '₹' + dynamicRejectionPayout
                          : REJECTION_PAYOUT
                        : DYNAMIC_REJECTION_PAYOUT}
                      {i18n.t('recommendationCard.rejectionPay')}
                    </B4>
                  </XStack>
                )}
              {(partnerId === DEMO_BRAND_PARTNER_ID || isTryDemoSellFlow) && (
                <XStack className="border-grays-40 pt-4/2 mt-2 items-center space-x-2 border-t border-dashed">
                  <Icon
                    type="InfoFilled"
                    size={20}
                    color={zetColors.warning[150]}
                  />
                  <Stack className="flex-1">
                    <B4>{i18n.t('recommendationCard.demoBrandPayout')}</B4>
                  </Stack>
                </XStack>
              )}
              {Boolean(isCardOnCard) && (
                <Stack
                  className="mt-4/2 rounded p-2"
                  style={{ backgroundColor: zetColors.decorative.bee[10] }}
                >
                  <CardOnCardText />
                </Stack>
              )}
              {/* TODO: Commented for now as this coupon is removed for few days */}
              {/* {partnerId?.toString() === CREDIT_BEE && (
                <ImageBackground
                  source={require('../../assets/images/coupon.png')}
                  resizeMode="cover"
                  className="mt-4/2"
                >
                  <B2 className="text-grays-60 ml-3 px-3">
                    {i18n.t('recommendationCard.offCode')}
                  </B2>
                </ImageBackground>
              )} */}
              {/* // TODO: This tag is for next sprint work */}
              {/* {!isCustomerFlow() && (
                <XStack className="bg-grays-20 mt-4/2 items-center space-x-2 rounded px-3 py-2">
                  <Icon type="AnnounceOutlined" className="relative" />
                  <B2>{i18n.t('recommendationCard.delayReport')}</B2>
                </XStack>
              )} */}
            </>
          )}
          {isCustomerFlow() && !isInEligible && (
            <XStack className="bg-success-10 mt-2 items-center space-x-2 rounded px-3 py-2">
              <Icon type="DoneOutlined" color={zetColors.success[100]} />
              <B2 className="text-success-100">
                {i18n.t('recommendationCard.eligibleText')}
              </B2>
            </XStack>
          )}
          {isCustomerFlow() && !REASON_LIST.includes(criteria as string) && (
            <WebApplyButton
              partnerId={partnerId as string}
              alreadyApplied={alreadyApplied as boolean}
              isSourceWeb={isSourceWeb as boolean}
              isInEligible={isInEligible as boolean}
              customerId={params?.customerId || (customerId as number)}
              index={index}
              customerPhoneNumber={
                params?.customerPhoneNumber || customerPhoneNumber
              }
              leadAdded={leadAdded}
              customerName={params?.customerName || customerName}
            />
          )}
        </YStack>
      </Pressable>
      {Boolean(isMoreCard) && (
        <YStack>
          <Stack className="border-grays-30 w-100 relative mx-4 h-2 rounded-b-lg border border-t-0" />
          <Stack
            className="border-grays-30 w-100 relative h-2 rounded-b-lg border border-t-0"
            style={{ marginHorizontal: 30 }}
          />
          <Stack className="items-center justify-center">
            <Pressable
              className="my-3 rounded-md border py-2"
              style={{ width: 160 }}
              onPress={handleViewAllPress}
              eventName="click_action"
              eventPayload={{
                item_name:
                  'brand_list_recommendation_view_all_ineligible_brands',
              }}
            >
              <BTN_SM className="text-grays-100 text-center">
                {i18n.t('viewAll')}
              </BTN_SM>
            </Pressable>
          </Stack>
        </YStack>
      )}
      {isRecommendationFlow &&
        isBrandDetailBSOpen &&
        APP_NAME !== MY_TEAMS_ADVISOR && (
          <BrandDetailsBottomSheet
            isBottomSheetVisible={isBrandDetailBSOpen}
            setIsBottomSheetVisible={setIsBrandDetailBSOpen}
            navigate={navigationRef.navigate}
            brandId={partnerId}
            isEligible={true}
            showShareButton={true}
            viewBrandDetailsButton={false}
            alreadyApplied={alreadyApplied as boolean}
            isSourceWeb={isSourceWeb as boolean}
            isInEligible={isInEligible as boolean}
            customerId={params?.customerId || (customerId as number)}
            index={index}
            customerPhoneNumber={
              params?.customerPhoneNumber || customerPhoneNumber
            }
            leadAdded={leadAdded}
            customerName={params?.customerName || customerName}
          />
        )}
    </Stack>
  )
}

function arePropsEqual(prevProps: IProductCard, nextProps: IProductCard) {
  return (
    prevProps?.partnerId === nextProps?.partnerId &&
    prevProps?.leadAdded === nextProps?.leadAdded &&
    prevProps?.reason === nextProps?.reason &&
    prevProps?.loyaltyPayout === nextProps?.loyaltyPayout
  )
}

const Component =
  Platform.OS === 'web'
    ? React.memo(ProductCard)
    : React.memo(ProductCard, arePropsEqual)

export default Component
